import React from 'react'
import type { ReactNode } from 'react'
import { HorizontalStack } from '@ztechapps/webui.util'
import type { PivotCellRenderer } from '../constants/types'
import { PIVOT_TOTAL_SUFFIX } from '../pivot/constants'
import styles from './PivotGridRow.module.scss'

/** Params for `renderPivotMemberLabel`. */
export type RenderPivotMemberLabelParams = {
  renderCell?: PivotCellRenderer
  field?: string
  label: string
  isTotal: boolean
  isBlank: boolean
}

/**
 * Renders one pivot row or column member label, shared by `PivotGridHead` and `PivotGridRow` so
 * both stay in sync.
 *
 * A blank member or a member with no field (e.g. the Grand Total header) shows `label` as-is.
 * Otherwise the field's `renderCell` gets the unsuffixed `label`. When `renderCell` declines
 * (returns `undefined`) or the member is blank, the plain fallback — `label`, with its own
 * ` Total` suffix appended for a subtotal — is shown instead.
 *
 * When `renderCell` does render something, the suffix is laid out in a `HorizontalStack`
 * alongside it, keeping " Total" on the same line as the rendered content — the suffix stays a
 * bare text node, never its own wrapping element, so a block-level `renderCell` result (e.g. a
 * link component) never wraps onto its own line away from the suffix.
 *
 * The stack carries `text-truncate` and its first child the `flexTruncate` (`min-width: 0`)
 * class: the rendered part is the child that shrinks and ellipsizes, not the fixed-width suffix.
 * The plain-text fallback needs none of this — it is a single flowing string, so the caller's
 * own `text-truncate` span ellipsizes it directly.
 */
export const renderPivotMemberLabel = ({
  renderCell,
  field,
  label,
  isTotal,
  isBlank,
}: RenderPivotMemberLabelParams): ReactNode => {
  const rendered = field !== undefined && !isBlank ? renderCell?.({ field, label }) : undefined
  const fallback = isTotal ? `${label} ${PIVOT_TOTAL_SUFFIX}` : label
  if (rendered === undefined) return fallback
  if (!isTotal) return rendered
  return (
    <HorizontalStack gap={1} className="text-truncate">
      <span className={`text-truncate ${styles.flexTruncate}`}>{rendered}</span> {PIVOT_TOTAL_SUFFIX}
    </HorizontalStack>
  )
}
